import { create } from 'zustand'
import { useAppPreferencesStore } from './useAppPreferencesStore'

export type PaymentMethodType =
  | 'ApplePay'
  | 'GooglePay'
  | 'Card'
  | 'OnlineBanking'
  | 'apple-pay'
  | 'google-pay'
  | 'card'
  | 'bank-transfer'

// Corresponds to WalletCurrency from the React Native app
export interface WalletCurrency {
  id: string
  name: string
  code: string
  amount: number
  amountWithoutNumberConversion: string
  icon: string
  label: string
  fiatAmount: number
  fiatCurrency: string
  percentageChange24h?: number
  revenue24hFiatAmount?: number
  pricePerUnit: number
  ceilingValueForValidation: number
  availableOnRampPaymentMethods: PaymentMethodType[]
  // Adapted fields for display
  formattedFiatBalance: string
  formattedAmountBalance: string
  formattedUSDBalance: string
  formattedStablecoinBalance: string
  usdBalance: number
  displayCurrencyCode: string
  symbol: string
  avatarBGRefColor: string | [string, string, ...string[]]
  // OnRamp limits
  minForOnRamp?: {
    fiat_currency: string
    fiat_amount: string
    formattedAmount: string
  }
  maxForOnRamp?: {
    fiat_currency: string
    fiat_amount: string
    formattedAmount: string
  }
}

// For backward compatibility
export type CurrencyData = WalletCurrency

// Amount types (same as RN version)
export interface AmountData {
  amount: string
  code: string
}

export interface AddMoneyState {
  // Selected currency (default USDT)
  selectedCurrency: CurrencyData | null

  // Payment method (Apple Pay, Card, etc.)
  paymentMethod: PaymentMethodType | null

  // Input mode (fiat or crypto)
  isFiatMode: boolean

  // Fiat currency amount
  fiatAmount: AmountData

  // Crypto currency amount
  cryptoAmount: AmountData

  // Shake animation trigger
  triggerShake: boolean

  // Loading
  isLoading: boolean

  // Errors
  error: string | null

  // Currency selection modal state
  isCurrencyModalOpen: boolean

  // Legacy fields for backward compatibility
  amount: string
  amountInUSD: string
}

export interface AddMoneyActions {
  setSelectedCurrency: (currency: CurrencyData) => void
  setPaymentMethod: (method: PaymentMethodType) => void
  setIsFiatMode: (isFiatMode: boolean) => void
  setCryptoAmount: (cryptoAmount: AmountData) => void
  setFiatAmount: (fiatAmount: AmountData) => void
  shakeComponent: () => void
  setIsLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setIsCurrencyModalOpen: (isOpen: boolean) => void
  reset: () => void
  fullReset: () => void

  // Legacy methods for backward compatibility
  setAmount: (amount: string) => void
  setAmountInUSD: (amount: string) => void
}

const initialState: AddMoneyState = {
  selectedCurrency: {
    id: '1',
    name: 'Tether',
    code: 'USDT',
    amount: 0,
    amountWithoutNumberConversion: '0',
    icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/usdt.png',
    label: '$',
    fiatAmount: 0,
    fiatCurrency: 'USD',
    pricePerUnit: 1,
    ceilingValueForValidation: 0,
    availableOnRampPaymentMethods: [
      'card',
      'apple-pay',
      'google-pay',
      'bank-transfer',
    ],
    formattedFiatBalance: '$0.00',
    formattedAmountBalance: '0',
    formattedUSDBalance: '$0.00',
    formattedStablecoinBalance: '0',
    usdBalance: 0,
    displayCurrencyCode: 'USDT',
    symbol: '$',
    avatarBGRefColor: '#33CC67',
  },
  paymentMethod: null,
  isFiatMode: true,
  triggerShake: false,
  fiatAmount: {
    amount: '0',
    code: 'USD', // Will be updated on displayCurrency initialization
  },
  cryptoAmount: {
    amount: '0',
    code: 'USDT',
  },
  isLoading: false,
  error: null,
  isCurrencyModalOpen: false,
  // Legacy fields for backward compatibility (synced with fiatAmount)
  amount: '0',
  amountInUSD: '0',
}

export const useAddMoneyStore = create<AddMoneyState & AddMoneyActions>(
  (set) => ({
    ...initialState,

    setSelectedCurrency: (currency) =>
      set((state) => {
        // If currency changed, clear amounts
        const currencyChanged = state.selectedCurrency?.code !== currency.code

        if (currencyChanged) {
          // Get displayCurrency from store
          const displayCurrency =
            useAppPreferencesStore.getState().displayCurrency
          const resetFiatAmount = {
            amount: '0',
            code: displayCurrency?.isin || 'USD',
          }
          const resetCryptoAmount = { amount: '0', code: currency.code }

          return {
            selectedCurrency: currency,
            fiatAmount: resetFiatAmount,
            cryptoAmount: resetCryptoAmount,
            amount: state.isFiatMode ? '0' : '0',
            amountInUSD: '0',
          }
        }

        // If currency didn't change, just update selectedCurrency
        return { selectedCurrency: currency }
      }),

    setPaymentMethod: (method) => set({ paymentMethod: method }),

    setIsFiatMode: (isFiatMode) =>
      set((state) => ({
        isFiatMode,
        // Sync amount when switching mode
        amount: isFiatMode
          ? state.fiatAmount.amount
          : state.cryptoAmount.amount,
      })),

    setCryptoAmount: (cryptoAmount) =>
      set((state) => ({
        cryptoAmount,
        // Sync with legacy fields for backward compatibility
        amount: state.isFiatMode
          ? state.fiatAmount.amount
          : cryptoAmount.amount,
      })),

    setFiatAmount: (fiatAmount) =>
      set((state) => ({
        fiatAmount,
        // Sync with legacy fields for backward compatibility
        amount: state.isFiatMode
          ? fiatAmount.amount
          : state.cryptoAmount.amount,
        amountInUSD: fiatAmount.amount,
      })),

    shakeComponent: () => {
      set({ triggerShake: true })
      setTimeout(() => set({ triggerShake: false }), 600)
    },

    setIsLoading: (loading) => set({ isLoading: loading }),

    setError: (error) => set({ error }),

    setIsCurrencyModalOpen: (isOpen) => set({ isCurrencyModalOpen: isOpen }),

    reset: () =>
      set((state) => ({
        ...initialState,
        // Preserve paymentMethod and selectedCurrency on reset
        paymentMethod: state.paymentMethod,
        selectedCurrency: state.selectedCurrency,
        // Ensure amount is synced on reset
        amount: initialState.isFiatMode
          ? initialState.fiatAmount.amount
          : initialState.cryptoAmount.amount,
      })),

    fullReset: () =>
      set(() => ({
        ...initialState,
        // Ensure amount is synced on reset
        amount: initialState.isFiatMode
          ? initialState.fiatAmount.amount
          : initialState.cryptoAmount.amount,
      })),

    // Legacy methods for backward compatibility
    setAmount: (amount) => set({ amount }),

    setAmountInUSD: (amount) => set({ amountInUSD: amount }),
  }),
)
